import React, { useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Stack } from "ui/stack";

import { useDisclosure } from "hooks/use-disclosure";

import { TextField } from "ui/text-field";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";
import PageContent from "components/partials/page-content/page-content";
import { Button } from "ui/button";
import { Breadcrumbs } from "ui/breadcrumbs";

import { ClientBasicInfoCard } from "containers/admin/clients/client/components/client-basic-info-card/client-basic-info-card";
import { ClientParentEmailCard } from "containers/admin/clients/client/components/client-parent-email-card/client-parent-email-card";

import { useCurrentClient, useDeleteClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";
import { getErrorMessage } from "utilities/index";
import { getClientPaths } from "utilities/app-routes";

import { Permission } from "types/auth";
import Icons from "assets";

export const ProfilePage = () => {
  const client = useCurrentClient();
  const currentUser = useCurrentUser();
  const confirmCancelModal = useDisclosure();
  const navigate = useNavigate();
  const deleteClientMutation = useDeleteClient();
  const { clientId } = useParams<{ clientId: string }>();
  const { clientCampaignsPath } = getClientPaths({ clientId });

  const [inputValue, setInputValue] = useState("");

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
  }, []);

  const handleClientDelete = () => {
    deleteClientMutation
      .mutateAsync({ id: client.id })
      .then(() => {
        toast.success({
          data: {
            title: `Deleted ${client.name}`,
          },
        });
        navigate("/clients");
      })
      .catch((err) => {
        toast.error({
          data: {
            title: `Failed to delete ${client.name}`,
            message: getErrorMessage(err?.response?.data),
          },
        });
      });
    confirmCancelModal.onClose();
  };

  return (
    <>
      <PageContent className="py-5">
        <Stack>
          <Breadcrumbs
            sx={{ mb: 2 }}
            breadcrumbs={[
              { label: client?.name, href: clientCampaignsPath },
              { label: "College profile", active: true },
            ]}
          />
          <div className="flex gap-4">
            <ClientBasicInfoCard
              client={client}
              actions={
                hasPermission(currentUser, Permission.PERM_CLIENT_WRITE) ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icons.Trash />}
                    onClick={confirmCancelModal.onOpen}>
                    Delete
                  </Button>
                ) : null
              }
            />
            <ClientParentEmailCard client={client} />
          </div>
        </Stack>
      </PageContent>
      <ConfirmationDialog
        open={confirmCancelModal.isOpen}
        onClose={() => {
          setInputValue("");
          confirmCancelModal.onClose();
        }}
        headline="Delete client"
        message={
          <>
            This will remove the client from Apollo permanently. This action cannot be undone. This
            will not automatically remove the client's data from related systems, and a request
            should be made to{" "}
            <Link
              className="text-navy-800 underline"
              to="#"
              onClick={(e) => {
                window.location.href =
                  "mailto:support@marketviewedu.com?subject=Remove all client's data";
                e.preventDefault();
              }}
            >
              support@marketviewedu.com
            </Link>{" "}
            to finish off-boarding the client.
          </>
        }
        cancelButtonText="Cancel"
        confirmButtonText="Submit"
        onConfirm={handleClientDelete}
        intent="warning"
        disabled={inputValue !== client?.name}
      >
        <TextField
          value={inputValue}
          id="client-name"
          data-testid="client-name-input"
          label="Client name"
          helperText="Please confirm this action by typing client name."
          onChange={handleChange}
        />
      </ConfirmationDialog>
    </>
  );
};
